import {
  FilterOutlined,
  ShopOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import {
  Form,
  Input,
  Button,
  Select,
  type FormProps,
  Flex,
  theme,
  Radio,
  DatePicker,
  Spin,
  Typography,
  Tag,
} from "antd";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { CrudFilters } from "@refinedev/core";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { axiosInstance, getStatuses } from "@/utilities";
import { OperatorsFilter, parseOperatorsFilterValue } from "@/components";
import { API_URL_CONTACT_GROUPS } from "@/services/XCONS_ENV";
import esES from "antd/es/date-picker/locale/es_ES";


const { RangePicker } = DatePicker;
const { Text } = Typography;
const { Option, OptGroup } = Select;

export const ContactsFilters: React.FC<{
  searchFormProps: FormProps;
  filters?: CrudFilters;
  sellersSelectProps?: any;
  storesSelectProps?: any;
  selectedStores: any;
  handleChangeStores?: any;
  t?: any;
  setSelectedContact?: any;
  userContactId?: number[] | null;
  /** Avisa que el operador eligió "Todos", para que el listado suelte el filtro que vino por URL. */
  onParticipacionLimpiada?: () => void;
}> = ({
  searchFormProps,
  filters,
  selectedStores,
  t,
  userContactId,
  onParticipacionLimpiada,
}) => {
    const [calendarValue, setCalendarValue] = useState<any>();
    const [showRangePicker, setShowRangePicker] = useState(false);
    const [calendarValueCreated, setCalendarValueCreated] = useState<any>();
    const [showRangePickerCreated, setShowRangePickerCreated] = useState(false);
    const [groups, setGroups] = useState<any>();
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [radioValue, setRadioValue] = useLocalStorage<any>("RadioCalendar", []);
    const [radioValueCreated, setRadioValueCreated] = useLocalStorage<any>(
      "RadioCalendarCreated",
      [],
    );

    const STATUSES = getStatuses(t);
    const { token } = theme.useToken();

    const [radioValueParticipant, setRadioValueParticipant] = useState<
      "all" | "me"
    >("all");

    const participantPresets = useMemo(
      () => [
        { label: t("filter.contact.allContacts"), value: "all" as const },
        { label: t("filter.contact.me"), value: "me" as const },
      ],
      [t],
    );

    const handleRadioChangeParticipant = (e: any) => {
      const val = e.target.value as "all" | "me";
      setRadioValueParticipant(val);

      const hasContactId = userContactId !== undefined && userContactId !== null && userContactId.length > 0;

      if (val === "me" && hasContactId) {
        // Pass the full array; onSearch will choose eq vs in depending on length
        searchFormProps.form?.setFieldsValue({
          participant_toggle: userContactId,
        });
      } else {
        searchFormProps.form?.setFieldsValue({ participant_toggle: undefined });
        // El listado sostiene aparte el filtro que vino por URL, porque con el drawer cerrado el
        // campo del formulario no existe. Elegir "Todos" es la señal explícita para soltarlo.
        onParticipacionLimpiada?.();
      }
    };

    // Al abrir el drawer con un filtro de operadores aplicado que el formulario no conoce (se llegó
    // por URL, o el drawer se destruyó), se rehidrata desde los ids; los nombres no viajan en la URL.
    useEffect(() => {
      const f = filters?.find((x: any) => x.field === "operators");
      const actual = searchFormProps.form?.getFieldValue("operators");
      if (f?.value && !(Array.isArray(actual) && actual.length)) {
        searchFormProps.form?.setFieldsValue({
          operators: parseOperatorsFilterValue(f.value),
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    useEffect(() => {
      const f = filters?.find((x: any) => x.field === "participations.contactId");

      const hasContactId = userContactId !== undefined && userContactId !== null && userContactId.length > 0;

      /**
       * Mientras las fichas propias no llegaron, **no se toca nada**.
       *
       * `userContactId` se resuelve con una request. Si el efecto corre antes de que vuelva y hay un
       * filtro de participación en la URL, caía en el `else`, apagaba el toggle, y el `onSearch`
       * siguiente reconstruía los filtros sin él: el filtro de la URL se aplicaba una vez y
       * desaparecía sola. Es el bug de QA #273.8 — llegar al listado desde "Clientes relacionados"
       * mostraba el recorte correcto por un instante y después la lista entera.
       */
      if (f?.value !== undefined && !hasContactId) return;

      if (f?.value !== undefined && hasContactId) {
        // The filter value can be a single id (eq) or an array (in)
        const filterIds = Array.isArray(f.value)
          ? f.value.map(Number)
          : [Number(f.value)];
        const isMe = filterIds.some((id) => userContactId.includes(id));

        if (isMe) {
          setRadioValueParticipant("me");
          searchFormProps.form?.setFieldsValue({
            participant_toggle: userContactId,
          });
        } else {
          setRadioValueParticipant("all");
          searchFormProps.form?.setFieldsValue({ participant_toggle: undefined });
        }
      } else {
        setRadioValueParticipant("all");
        searchFormProps.form?.setFieldsValue({ participant_toggle: undefined });
      }
    }, [filters, userContactId]);

    const getGroups = async () => {
      setLoadingGroups(true);
      try {
        const response = await axiosInstance.get(API_URL_CONTACT_GROUPS);
        const g = response.data.data;
        setGroups(g);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingGroups(false);
      }
    };

    useEffect(() => {
      getGroups();
    }, []);

    const handleRangeChange = (dates: null | (Dayjs | null)[]) => {
      if (dates) {
        searchFormProps.form?.setFieldsValue({ calendar: dates });
      } else {
        searchFormProps.form?.setFieldsValue({ calendar: undefined });
      }
    };

    const handleRangeChangeCreated = (dates: null | (Dayjs | null)[]) => {
      if (dates) {
        searchFormProps.form?.setFieldsValue({ calendar_created: dates });
      } else {
        searchFormProps.form?.setFieldsValue({ calendar_created: undefined });
      }
    };

    useEffect(() => {
      searchFormProps.form?.setFieldsValue({ calendar: calendarValue });
    }, [calendarValue]);

    useEffect(() => {
      searchFormProps.form?.setFieldsValue({
        calendar_created: calendarValueCreated,
      });
    }, [calendarValueCreated]);

    const thisToday: [Dayjs, Dayjs] = [
      dayjs().startOf("day"),
      dayjs().endOf("day"),
    ];

    const thisWeek: [Dayjs, Dayjs] = [
      dayjs().startOf("week"),
      dayjs().endOf("week"),
    ];
    const thisMonth: [Dayjs, Dayjs] = [
      dayjs().startOf("month"),
      dayjs().endOf("month"),
    ];

    const rangePresets = useMemo(
      () => [
        { label: t("filter.calendar.all"), value: "" },
        { label: t("filter.calendar.today"), value: "today" },
        { label: t("filter.calendar.thisWeek"), value: "thisweek" },
        { label: t("filter.calendar.thisMonth"), value: "thismonth" },
        { label: t("filter.calendar.personalized"), value: "personalized" },
      ],
      [t],
    );

    useEffect(() => {
      if (filters) {
        /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
        const calendarFilter = filters?.find((f) => f.field === "updatedAt");
        const calendarFilterCreated = filters?.find(
          /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
          (f) => f.field === "createdAt",
        );

        if (calendarFilter) {
          const [start, end] = calendarFilter.value || [];
          if (start && end) {
            setCalendarValue([dayjs(start), dayjs(end)]);
          } else {
            setCalendarValue(undefined);
            setRadioValue("");
            setShowRangePicker(false);
          }
        }

        if (calendarFilterCreated) {
          const [start, end] = calendarFilterCreated.value || [];
          if (start && end) {
            setCalendarValueCreated([dayjs(start), dayjs(end)]);
          } else {
            setCalendarValueCreated(undefined);
            setRadioValueCreated("");
            setShowRangePickerCreated(false);
          }
        }
      }
    }, [filters]);

    const handleRadioChange = useCallback(
      (e: any) => {
        const { value } = e.target;
        setRadioValue(value);

        if (value === "personalized") {
          setShowRangePicker(true);
          setCalendarValue(value);
        } else if (value === "today") {
          setShowRangePicker(false);
          setCalendarValue(thisToday);
        } else if (value === "thisweek") {
          setShowRangePicker(false);
          setCalendarValue(thisWeek);
        } else if (value === "thismonth") {
          setShowRangePicker(false);
          setCalendarValue(thisMonth);
        } else {
          setShowRangePicker(false);
          setCalendarValue(undefined);
        }
      },
      [setRadioValue, setShowRangePicker, setCalendarValue],
    );

    const handleRadioChangeCreated = useCallback(
      (e: any) => {
        const { value } = e.target;
        setRadioValueCreated(value);

        if (value === "personalized") {
          setShowRangePickerCreated(true);
          setCalendarValueCreated(value);
        } else if (value === "today") {
          setShowRangePickerCreated(false);
          setCalendarValueCreated(thisToday);
        } else if (value === "thisweek") {
          setShowRangePickerCreated(false);
          setCalendarValueCreated(thisWeek);
        } else if (value === "thismonth") {
          setShowRangePickerCreated(false);
          setCalendarValueCreated(thisMonth);
        } else {
          setShowRangePickerCreated(false);
          setCalendarValueCreated(undefined);
        }
      },
      [setRadioValueCreated, setShowRangePickerCreated, setCalendarValueCreated],
    );

    function clearFilters() {
      searchFormProps.form?.resetFields();
      setCalendarValue(undefined);
      setCalendarValueCreated(undefined);
      setRadioValue("");
      setRadioValueCreated("");
      setRadioValueParticipant("all");
      searchFormProps.form?.setFieldsValue({ participant_toggle: undefined });
      setShowRangePicker(false);
      searchFormProps.form?.submit();
    }

    const groupedFilterOptions: Record<string, any[]> = (groups || []).reduce(
      (acc: Record<string, any[]>, group: any) => {
        const store = group.storeCode;
        if (!acc[store]) {
          acc[store] = [];
        }
        acc[store].push(group);
        return acc;
      },
      {},
    );


    return (
      <Form layout="vertical" {...searchFormProps} style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
        <Form.Item name="participant_toggle" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("filter.contact.relation")}
            </span>
          }
        >
          <Flex vertical>
            <Radio.Group
              value={radioValueParticipant}
              style={{ display: "flex", gap: 10 }}
              size="small"
              onChange={handleRadioChangeParticipant}
            >
              {participantPresets.map((preset, i) => (
                <Radio.Button key={`${i}-participant`} value={preset.value}>
                  {preset.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("filter.calendar.lastCreate")}
            </span>
          }
          name="calendar_created"
          key="calendar_created_unique"
        >
          <Flex vertical>
            <Radio.Group
              value={radioValueCreated}
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: showRangePickerCreated ? "10px" : "",
              }}
              size="small"
              onChange={handleRadioChangeCreated}
            >
              {rangePresets.map((preset, i) => (
                <Radio.Button
                  key={`${i}-created`}
                  value={preset.value}
                  name={preset.value}
                >
                  {preset.label}
                </Radio.Button>
              ))}
            </Radio.Group>
            {showRangePickerCreated && (
              <RangePicker locale={esES} onChange={handleRangeChangeCreated} required />
            )}
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("filter.calendar.lastUpdate")}
            </span>
          }
          name="calendar"
          key="calendar_updated"
        >
          <Flex vertical>
            <Radio.Group
              value={radioValue}
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: showRangePicker ? "10px" : "",
              }}
              size="small"
              onChange={handleRadioChange}
            >
              {rangePresets.map((preset, i) => (
                <Radio.Button key={i} value={preset.value} name={preset.value}>
                  {preset.label}
                </Radio.Button>
              ))}
            </Radio.Group>
            {showRangePicker && (
              <RangePicker onChange={handleRangeChange} required />
            )}
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("contacts.table.relations")}
            </span>
          }
          name="operators"
        >
          {/* Fase 6 de filtros-operadores-clientes (xcons-docs#307): el campo pasa a ser sólo
              operadores, por afiliación, y sigue al store elegido arriba en vez de congelarlo. */}
          <OperatorsFilter
            storeCodes={(selectedStores ?? [])
              .map((s: any) => s?.code)
              .filter(Boolean)}
          />
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("contacts.table.groups")}
            </span>
          }
          name="contactGroups"
        >
          <Flex vertical>
            <Select
              loading={loadingGroups}
              allowClear
              mode="multiple"
              placeholder={t("filter.calendar.all")}
              optionLabelProp="labelTag"
              defaultValue={
                filters?.find(
                  (f) =>
                    // @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66
                    f.field === "contactGroupRelation.contactGroup.name",
                )?.value
              }
              onChange={(value) => {
                searchFormProps.form?.setFieldsValue({ contactGroups: value });
              }}
            >
              {Object.entries(groupedFilterOptions).map(
                ([storeCode, storeGroups]) => (
                  <OptGroup
                    key={storeCode}
                    label={
                      <Flex align="center" gap={6}>
                        <ShopOutlined />
                        <span style={{ textTransform: "capitalize" }}>
                          {storeCode}
                        </span>
                      </Flex>
                    }
                  >
                    {storeGroups.map((g) => (
                      <Option
                        key={g.contactGroupId}
                        value={g.name}
                        labelTag={g.name}
                      >
                        <Spin spinning={loadingGroups}>
                          <Tag
                            icon={<UsergroupAddOutlined />}
                            style={{ marginTop: 2, width: "fit-content" }}
                          >
                            {g.name}
                          </Tag>
                        </Spin>
                      </Option>
                    ))}
                  </OptGroup>
                ),
              )}
            </Select>
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>Compañía</span>
          }
          name="company"
        >
          <Input allowClear placeholder="Buscar compañía" />
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>Status</span>
          }
          name="archived_status"
          initialValue="active"
        >
          <Select
            options={[
              { label: "Active", value: "active" },
              { label: "Archived", value: "archived" },
              { label: "All", value: "all" },
            ]}
          />
        </Form.Item>

        {/* <Flex gap={8}>
        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>Quotes range</span>
          }
          name="quotes_price_product_from"
        >
          <InputNumber<number>
            addonBefore="$"
            min={0}
            style={{ width: "100%" }}
            placeholder={t("filter.products.labelPriceFrom")}
          />
        </Form.Item>
        <Form.Item
          label={
            <span style={{ color: "white" }}>
              {t("filter.products.labelPrice")}
            </span>
          }
          name="quotes_price_product_to"
        >
          <InputNumber<number>
            addonBefore="$"
            min={0}
            style={{ width: "100%" }}
            placeholder={t("filter.products.labelPriceTo")}
          />
        </Form.Item>
      </Flex> */}

        {/* <Flex gap={8}>
        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>Order ranges</span>
          }
          name="order_price_product_from"
        >
          <InputNumber<number>
            addonBefore="$"
            min={0}
            style={{ width: "100%" }}
            placeholder={t("filter.products.labelPriceFrom")}
          />
        </Form.Item>
        <Form.Item
          label={
            <span style={{ color: "white" }}>
              {t("filter.products.labelPrice")}
            </span>
          }
          name="order_price_product_to"
        >
          <InputNumber<number>
            addonBefore="$"
            min={0}
            style={{ width: "100%" }}
            placeholder={t("filter.products.labelPriceTo")}
          />
        </Form.Item>
      </Flex> */}

        </div>
        <Flex
          align="center"
          justify="end"
          gap="8px"
          style={{ flexShrink: 0, padding: "12px 24px", borderTop: "1px solid #f0f0f0", backgroundColor: "#fff" }}
        >
          <Button type="default" danger onClick={clearFilters}>
            {t("filter.button.clearFilters")}
          </Button>
          <Button htmlType="submit" type="primary" icon={<FilterOutlined />}>
            {t("filter.button.filter")}
          </Button>
        </Flex>
      </Form>
    );
  };
